import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { theme as baseTheme } from '@swagcharm/kiwi-components';
import mediaQuery from 'css-mediaquery';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import * as React from 'react';
import { hydrate } from 'react-dom';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { getInitialNamespaces } from 'remix-i18next';
import LDBrowser from '~/components/launchdarkly.client';
import createEmotionCache from '~/utils/emotion/createEmotionCache';
import { ClientStyleContext } from '~/utils/emotion/StylesContext';
import { commonInitValues } from '~/utils/i18n';

if (window.ENV.SENTRY_DSN) {
  Sentry.init({
    dsn: window.ENV.SENTRY_DSN,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.remixRouterInstrumentation(React.useEffect, useLocation, useMatches),
      }),
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: true,
      }),
    ],
    ignoreErrors: ['ResizeObserver loop limit exceeded'],
    release: window.ENV.APP_NAME + '@' + window.ENV.APP_VERSION,
    dist: 'client',
  });
}

if (window.ENV.LOGROCKET_ID && window.location.hostname !== 'localhost') {
  const LogRocket = require('logrocket');
  LogRocket.init(window.ENV.LOGROCKET_ID, {
    release: window.ENV.APP_VERSION,
  });

  if (window.ENV.SENTRY_DSN) {
    LogRocket.getSessionURL((sessionURL: string) => {
      Sentry.configureScope((scope) => {
        scope.setExtra('sessionURL', sessionURL);
      });
    });
  }
}

const MuiRemixClient: React.FC = ({ children }) => {
  const [cache, setCache] = React.useState(createEmotionCache());

  function reset() {
    setCache(createEmotionCache());
  }

  const ssrMatchMedia = (query: string) => ({
    matches: mediaQuery.match(query, {
      // The estimated CSS width of the browser.
      width: document.body.clientWidth,
    }),
  });

  const theme = createTheme({
    ...baseTheme,
    components: {
      ...baseTheme.components,
      // Change the default options of useMediaQuery
      MuiUseMediaQuery: {
        defaultProps: {
          ssrMatchMedia,
        },
      },
    },
  });

  return (
    <ClientStyleContext.Provider value={{ reset }}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
          <CssBaseline enableColorScheme />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </ClientStyleContext.Provider>
  );
};

i18next
  .use(initReactI18next) // use the react-i18next plugin
  .use(LanguageDetector) // setup a client-side language detector
  .use(Backend) // setup our backend
  .init({
    ...commonInitValues,
    // detects the namespaces your routes rendered while SSR use and pass them here to load the translations
    ns: getInitialNamespaces(),
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      // only enable htmlTag detection, we'll detect the language only
      // server-side with remix-i18next, by using the `<html lang>` attribute
      // we can communicate to the client the language detected server-side
      order: ['htmlTag'],
      // because we only use htmlTag, there's no reason to cache the language on the browser, so we disable it
      caches: [],
    },
  })
  .then(() => {
    // hydrate the app after i18next has been initialized
    // we need to wait to ensure translations are loaded before the hydration
    hydrate(
      <I18nextProvider i18n={i18next}>
        <MuiRemixClient>
          <LDBrowser>
            <RemixBrowser />
          </LDBrowser>
        </MuiRemixClient>
      </I18nextProvider>,
      document
    );
  });
